import { Injectable, inject } from '@angular/core';
import * as _ from 'lodash';
import {firstValueFrom, Observable, Subscription, distinctUntilChanged, map} from 'rxjs';
import {TranslateService} from '../../../modules/core/services';
import {SearchConfig, SearchPropertyKey, SearchResultItem, SearchStrategy, Vehicle} from '../../shared/models';
import {Line} from '../../shared/models/line';
import {LineStore} from './line.store';
import {Store} from './store.model';
import {VehicleStore} from './vehicle.store';
import {AppStateService} from "@modules/core/services/app-state.service";
import {Garage} from "@shared/models/garage";
import { NotificationService } from '@modules/shared/services/notification.service';

export interface SearchStoreState {
    searchResults: SearchResultItem[];
}
@Injectable({
    providedIn: 'root'
})
export class SearchStore extends Store<SearchStoreState> {
    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _lineStore = inject(LineStore);
    private readonly _translateService = inject(TranslateService);
    private readonly _appStateService = inject(AppStateService);
    private readonly _notificationService = inject(NotificationService);

    private readonly _backendSearchSubscription: Subscription;


    constructor() {
        super({
            searchResults: []
        });
    }

    public getPropertyState<K extends keyof SearchStoreState>(propertyKey: K): Observable<SearchResultItem[]> {
        return super.state$.pipe(
            map(state => state[propertyKey]),
            distinctUntilChanged()
        );
    }

    public abortLastSearch() {
        if (this._backendSearchSubscription) {
            this._backendSearchSubscription.unsubscribe();
        }
    }

    public async searchAsync(query: string, searchConfigs: SearchConfig[] = null) {
        const selectedGarages = await firstValueFrom(this._appStateService.getSelectedGaragesState());

        if (!selectedGarages?.length) {
            this._notificationService.openText(
                this._translateService.instant('No garages selected!')
            );
        }

        const selectedGarageLookup = selectedGarages.reduce((lookup, garage) => {
            lookup[garage.code.toLowerCase()] = garage;
            return lookup;
        }, {} as Record<string, Garage>);

        // Search through vehicle dataset for inventoryNumber and blockNumber
        let searchResults = this._getSearchResultsFromVehicles(query, selectedGarageLookup, searchConfigs) || [];

        // Search through lines dataset for routeNumber
        searchResults = searchResults.concat(this._getSearchResultsFromLines(query, selectedGarageLookup, searchConfigs));

        searchResults = SearchStore._moveExactMatchToTheTop(query, searchResults);

        this.setState({
            ...this.stateSnapshot,
            searchResults
        });
    }

    private _getSearchResultsFromVehicles(query: string, selectedGaragesLookup: Record<string, Garage>, searchConfigs?: SearchConfig[]): SearchResultItem[] {
        const iterableVehicles = this._vehicleStore.stateSnapshot.vehicles?.values();
        if (!iterableVehicles) return [];

        const vehicles = Array.from(iterableVehicles);
        if (!vehicles?.length) {
            return [];
        }

        searchConfigs = searchConfigs ?? [
            {
                searchPropertyKey: SearchPropertyKey.inventoryNumberWithoutPrefix,
                searchStrategy: SearchStrategy.ExactSearch,
                minQueryLength: 3,
                fuzzySearchConfig: {
                    tolerance: 1
                }
            },
            {
                searchPropertyKey: SearchPropertyKey.blockNumber,
                searchStrategy: SearchStrategy.ExactSearch,
                minQueryLength: 1,
            }
        ];

        const queryInLowerCase = query.toLowerCase();
        const searchResultItems: SearchResultItem[] = [];
        _.each(vehicles, (vehicle) => {
            const searchMatchingKeys = this._getObjectSearchMatchKeys(vehicle, queryInLowerCase, searchConfigs);
            if (searchMatchingKeys.length <= 0) {
                return;
            }

            _.each(searchMatchingKeys, key => {
                searchResultItems.push(this._getSearchResultItemFromVehicle(vehicle, key, query, selectedGaragesLookup));
            });
        });

        return searchResultItems;
    }

    private _getSearchResultsFromLines(query: string, selectedGaragesLookup: Record<string, Garage>, searchConfigs?: SearchConfig[]): SearchResultItem[] {
        const iterableLines = this._lineStore.stateSnapshot.lines?.values();
        if (!iterableLines) return [];

        const lines = Array.from(iterableLines);
        if (!lines?.length) {
            return [];
        }

        searchConfigs = searchConfigs ?? [
            {
                searchPropertyKey: SearchPropertyKey.routeNumber,
                searchStrategy: SearchStrategy.ExactSearch,
                minQueryLength: 1,
            }
        ];

        const queryInLowerCase = query.toLowerCase();
        const searchResultItems: SearchResultItem[] = [];
        _.each(lines, (line) => {
            const searchMatchingKeys = this._getObjectSearchMatchKeys(line, queryInLowerCase, searchConfigs);
            if (searchMatchingKeys.length <= 0) {
                return;
            }

            _.each(searchMatchingKeys, key => {
                searchResultItems.push(this._getSearchResultItemFromLine(line, key, query, selectedGaragesLookup));
            });
        });

        return searchResultItems;
    }

    private _getObjectSearchMatchKeys(object: any, query: string, searchConfigs: SearchConfig[]): SearchPropertyKey[] {
        if (!object) {
            return null;
        }

        let hits: SearchPropertyKey[] = [];
        _.each(searchConfigs, searchConfig => {
            let propertyValue: any = object[searchConfig.searchPropertyKey];
            if (!SearchStore._isString(propertyValue)) {
                return;
            }

            propertyValue = propertyValue.toLowerCase();
            if (searchConfig.searchStrategy === SearchStrategy.FuzzySearch
                && SearchStore._isFuzzyMatch(query, propertyValue, searchConfig.minQueryLength, searchConfig.fuzzySearchConfig.tolerance)) {
                hits.push(SearchPropertyKey[searchConfig.searchPropertyKey])
            } else if (searchConfig.searchStrategy === SearchStrategy.ExactSearch
                && SearchStore._isExactMatch(query, propertyValue, searchConfig.minQueryLength)) {
                hits.push(SearchPropertyKey[searchConfig.searchPropertyKey])
            }
        });

        return hits;
    }

    // builds content for search result presentation, depending on which type of item the search found
    private _getSearchResultItemFromVehicle(vehicle: Vehicle, matchingPropertyKey: SearchPropertyKey, query: string, selectedGaragesLookup: Record<string, Garage>): SearchResultItem {
        let value: string; let id: string; let iconClass: any; let type: string; let subTitle: any;

        // Get some extra traffic area and garage info, to display in the subtitle
        let garage: Garage = null;
        if (SearchStore._isString(vehicle.garageCode)) {
            const vehicleGarageCode = vehicle.garageCode.toLowerCase();
            garage = selectedGaragesLookup[vehicleGarageCode];
        }

        const garageInfoString = garage == undefined || garage.trafficAreaName == undefined || garage.description == undefined
            ? ''
            : ` | ${garage.trafficAreaName} | ${garage.description}`;

        switch (matchingPropertyKey) {
            case SearchPropertyKey.blockNumber:
                value = `${vehicle.blockNumber}`;
                subTitle = this._translateService.instant('Block') + garageInfoString;
                id = vehicle.inventoryNumber;
                type = 'vehicle';
                break;
            default:
                value = vehicle.inventoryNumberWithoutPrefix;
                subTitle = this._translateService.instant('Vehicle') + garageInfoString;
                id = vehicle.inventoryNumber;
                type = 'vehicle';
                break;
        }

        const r: SearchResultItem = {
            title: this._makeSubStringBold(value, query),
            subTitle,
            value,
            tag: '',
            state: '',
            id,
            scrollableId: id,
            type,
            iconClass,
            matchingSearchKey: matchingPropertyKey,
            matchingQuery: query
        };
        return r;
    }

    private _getSearchResultItemFromLine(line: Line, matchingPropertyKey: SearchPropertyKey, query: string, selectedGaragesLookup: Record<string, Garage>): SearchResultItem {
        // Get some extra traffic area info to display in the subtitle
        let trafficAreaInfo = '';
        _.each(line.garageCodes, garageCode => {
            const garage = selectedGaragesLookup[garageCode.toLowerCase()];
            if (garage == undefined || garage.trafficAreaName == undefined) {
                return;
            } else if (trafficAreaInfo === '') {
                trafficAreaInfo = garage.trafficAreaName;
            } else if (trafficAreaInfo !== garage.trafficAreaName) {
                trafficAreaInfo = this._translateService.instant('Multiple Traffic Areas');
            }
        })
        const garageInfoString = trafficAreaInfo !== '' ? ` | ${trafficAreaInfo}` : '';

        const r: SearchResultItem = {
            title: this._makeSubStringBold(`${line.routeNumber}`, query),
            subTitle: this._translateService.instant('Route') + garageInfoString,
            value: line.routeNumber,
            tag: '',
            state: '',
            id: line.id,
            scrollableId: SearchStore.toScrollableId(line.id),
            type: 'line',
            iconClass: null,
            matchingSearchKey: matchingPropertyKey,
            matchingQuery: query
        };
        return r;
    }

    private _makeSubStringBold(fullString: string, substring: string) {
        const boldSubstring = `<strong>${substring}</strong>`;
        return _.replace(fullString, substring, boldSubstring);
    }

    public static toScrollableId(input: string): string {
        return input.replace(/[\s\.åäöøÅÄÖØ]/g, '-');
    }

    private static _moveExactMatchToTheTop(query: string, searchResults: SearchResultItem[]): SearchResultItem[] {

        // A helper function to calculate similarity between two strings
        const calculateSimilarity = (str1: string, str2: string): number => {
            // Use a basic similarity measure like Jaccard or Levenshtein
            const lengthDiff = Math.abs(str1.length - str2.length); // e.g penalize length differences
            const commonLength = [...str1].filter(char => str2.includes(char)).length;

            // A higher score indicates greater similarity
            return commonLength - lengthDiff;
        };

        query = query.toLowerCase();

        const sorted = searchResults.sort((a, b) => {
            const aValueLowered = a.value.toLowerCase();
            const bValueLowered = b.value.toLowerCase();

            // Move exact matches to the top.
            if (aValueLowered === query) return -1;
            if (bValueLowered === query) return 1;

            // Otherwise, sort based on similarity to the query.
            const similarityA = calculateSimilarity(aValueLowered, query);
            const similarityB = calculateSimilarity(bValueLowered, query);

            return similarityB - similarityA; // Higher similarity should come first.
        });

        return sorted;
    }

    /**
     * If the query is an exact match, or exact substring of the value that is being compared.
     * However, it also allows for a number of mismatching chars (e.g. misspelled query)
     */
    private static _isFuzzyMatch(query: string, value: string, minQueryLength: number = 3, tolerance: number = 1): boolean {
        return SearchStore._isExactMatch(query, value, minQueryLength) || this._levenshteinDistance(query, value) <= tolerance;
    }

    /**
     * If the query is an exact match, or exact substring of the value that is being compared
     */
    private static _isExactMatch(query: string, value: string, minQueryLength: number = 3): boolean {
        if (query === value) {
            return true;
        }

        if (query.length < minQueryLength) {
            return false;
        }

        return false
    }

    private static _levenshteinDistance(a: string, b: string): number {
        const matrix: number[][] = [];

        // Initialize the matrix
        for (let i = 0; i <= a.length; i++) {
            matrix[i] = [i];
        }
        for (let j = 0; j <= b.length; j++) {
            matrix[0][j] = j;
        }

        // Compute distances
        for (let i = 1; i <= a.length; i++) {
            for (let j = 1; j <= b.length; j++) {
                const cost = a[i - 1] === b[j - 1] ? 0 : 1;
                matrix[i][j] = Math.min(
                    matrix[i - 1][j] + 1, // Deletion
                    matrix[i][j - 1] + 1, // Insertion
                    matrix[i - 1][j - 1] + cost // Substitution
                );
            }
        }

        return matrix[a.length][b.length];
    }

    private static _isString(value: any): boolean {
        return typeof value === 'string' || value instanceof String;
    }
}
